import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Leadership from "@/pages/Leadership";
import Teams from "@/pages/Teams";
import Strategie from "@/pages/Strategie";
import Organisation from "@/pages/Organisation";
import About from "@/pages/About";
import LegalPage from "@/pages/LegalPage";
import { AnalyticsConsent } from "@/components/AnalyticsConsent";
import { RouteAnalytics } from "@/analytics/RouteAnalytics";
import { Schema } from "@/components/Schema";
import { PAGES, canonicalFor } from "@/seo/seoData";
import { scrollToAnchor } from "@/utils/scroll";

const useSmoothScroll = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
};

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    let timeout;
    if (hash) {
      let attempts = 0;
      const scrollToHash = () => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          scrollToAnchor(hash.slice(1));
          return;
        }
        if (attempts < 12) {
          attempts += 1;
          timeout = setTimeout(scrollToHash, 50);
        }
      };
      scrollToHash();
      return () => clearTimeout(timeout);
    }
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [pathname, hash]);
  return null;
};

const Seo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const key = PAGES[pathname] ? pathname : "/";
    document.title = PAGES[key].title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", PAGES[key].description);
    const canonical = canonicalFor(key);
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", canonical);
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", canonical);
  }, [pathname]);
  return null;
};

function App() {
  useSmoothScroll();
  return (
    <LanguageProvider>
      <BrowserRouter>
        <ScrollManager />
        <Seo />
        <Schema />
        <RouteAnalytics />
        <AnalyticsConsent />
        <div className="App">
          <Navigation />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/leadership" element={<Leadership />} />
            <Route path="/team" element={<Teams />} />
            <Route path="/teams" element={<Navigate to="/team" replace />} />
            <Route path="/strategie" element={<Strategie />} />
            <Route path="/organisation" element={<Organisation />} />
            <Route path="/ueber-mich" element={<About />} />
            <Route path="/impressum" element={<LegalPage page="impressum" />} />
            <Route path="/datenschutz" element={<LegalPage page="datenschutz" />} />
            <Route path="*" element={<Home />} />
          </Routes>
          <Footer />
        </div>
        <Toaster position="bottom-center" toastOptions={{ style: { background: "#032C37", color: "#fff", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 0 } }} />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
